import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Star, Quote, ExternalLink, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { cafeData } from '../data/cafeData';

export const Reviews = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="reviews-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 px-6 md:px-12 bg-white" data-testid="reviews-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#E07A5F] text-sm font-medium uppercase tracking-widest">Testimonials</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1C1917] mt-4 mb-6">
              What Our Customers Say
            </h1>
            <p className="text-stone-500 text-lg md:text-xl leading-relaxed">
              Don't just take our word for it – hear from our wonderful customers who've made Cafe 110 their local favourite.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Rating Summary */}
      <section className="py-16 px-6 md:px-12 bg-[#3A4D39]" data-testid="reviews-summary">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="font-serif text-6xl font-bold text-white">{cafeData.reviews.averageRating}</p>
              <div className="flex justify-center mt-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-white/70 mt-2">Average Rating</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <p className="font-serif text-6xl font-bold text-white">{cafeData.reviews.totalReviews}</p>
              <p className="text-white/70 mt-2">Total Reviews</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="w-20 h-20 rounded-full bg-green-500 flex items-center justify-center mx-auto" data-testid="reviews-hygiene-badge">
                <span className="text-white text-4xl font-bold">{cafeData.hygiene.rating}</span>
              </div>
              <p className="text-white/70 mt-3">Food Hygiene Rating</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Review */}
      <section className="py-20 md:py-32 px-6 md:px-12" data-testid="featured-review">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="relative bg-white p-10 md:p-16 border border-stone-100 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Quote className="w-12 h-12 text-[#E07A5F]/20 mx-auto mb-6" />
            <p className="font-serif text-2xl md:text-3xl text-[#1C1917] italic leading-relaxed mb-8">
              "{cafeData.reviews.highlights[0].quote}"
            </p>
            <div className="flex justify-center mb-4">
              {[...Array(cafeData.reviews.highlights[0].rating)].map((_, i) => (
                <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <p className="font-medium text-[#1C1917]">{cafeData.reviews.highlights[0].author}</p>
            <p className="text-stone-400 text-sm">{cafeData.reviews.highlights[0].date}</p>
          </motion.div>
        </div>
      </section>

      {/* All Reviews */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-white" data-testid="all-reviews">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1C1917]">
              Customer Reviews
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cafeData.reviews.highlights.map((review, index) => (
              <motion.div
                key={index}
                className="bg-[#FDFBF7] p-8 border border-stone-100 hover:shadow-lg transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`review-card-${index}`}
              >
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < review.rating
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-stone-300'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-stone-600 italic leading-relaxed mb-6">
                  "{review.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#3A4D39] flex items-center justify-center text-white font-medium">
                    {review.author.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-[#1C1917]">{review.author}</p>
                    <p className="text-stone-400 text-sm">{review.date}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#FDFBF7]" data-testid="trust-indicators">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="text-[#E07A5F] text-sm font-medium uppercase tracking-widest">Why Trust Us</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1C1917] mt-4">
              Quality You Can Trust
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: '5-Star Hygiene Rating',
                description: 'Inspected by Merton Council with top marks for food handling, cleanliness, and management.',
                detail: `Inspected ${cafeData.hygiene.inspectionDate}`
              },
              {
                title: 'Verified Reviews',
                description: 'Our reviews come from real customers who have visited and experienced our café.',
                detail: `${cafeData.reviews.totalReviews} verified reviews`
              },
              {
                title: 'Local Favourite',
                description: 'Proudly serving the South Wimbledon community with authentic Italian hospitality.',
                detail: 'Near South Wimbledon Station'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-white p-8 border border-stone-100 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h3 className="font-serif text-xl font-bold text-[#1C1917] mb-3">{item.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed mb-4">{item.description}</p>
                <p className="text-[#3A4D39] font-medium text-sm">{item.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#3A4D39]" data-testid="reviews-cta">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">
              Experience It Yourself
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Join our happy customers and discover why Cafe 110 is South Wimbledon's favourite Italian café.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/menu" data-testid="reviews-cta-menu-btn">
                <Button className="bg-[#E07A5F] hover:bg-[#d06a4f] text-white px-10 py-6 text-lg font-medium">
                  View Our Menu
                </Button>
              </Link>
              <Link to="/contact" data-testid="reviews-cta-visit-btn">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#3A4D39] px-10 py-6 text-lg">
                  Visit Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Review Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": cafeData.name,
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": cafeData.reviews.averageRating,
            "reviewCount": cafeData.reviews.totalReviews,
            "bestRating": "5",
            "worstRating": "1"
          },
          "review": cafeData.reviews.highlights.map(review => ({
            "@type": "Review",
            "author": {
              "@type": "Person",
              "name": review.author
            },
            "datePublished": review.date,
            "reviewBody": review.quote,
            "reviewRating": {
              "@type": "Rating",
              "ratingValue": review.rating,
              "bestRating": "5",
              "worstRating": "1"
            }
          }))
        })
      }} />
    </div>
  );
};
